/**
 * ProjectHeroCarousel - Core Component
 * 
 * A modern hero section featuring text content on the left and an Apple TV-style
 * card carousel on the right. Projects rotate automatically with smooth transitions.
 * 
 * Features:
 * - Split layout: Text left, carousel right
 * - Apple TV-style stacked cards
 * - Auto-rotation with pause on hover
 * - Smooth micro-interactions
 * - Theme-agnostic (styled via className props)
 * - Multi-language support
 * - Responsive design
 * - Accessibility friendly
 */

'use client';

import { cn } from '@/lib/utils';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import type { PropertyDevelopmentProject } from '@/lib/api/clients/projects';

export interface ProjectHeroCarouselProps {
  /** Featured projects to display in carousel */
  projects: PropertyDevelopmentProject[];
  /** Hero title */
  title?: string;
  /** Hero subtitle/description */
  subtitle?: string;
  /** Badge text (e.g., "Featured Projects") */
  badgeText?: string;
  /** Stats to display (optional) */
  stats?: Array<{
    label: string;
    value: string | number;
  }>;
  /** CTA button text */
  ctaText?: string;
  /** CTA button link */
  ctaLink?: string;
  /** Messages for translations */
  messages?: Record<string, unknown> | {
    badge?: string;
    title?: string;
    subtitle?: string;
    viewProject?: string;
    availableUnits?: string;
    soldOut?: string;
    comingSoon?: string;
    startingFrom?: string;
  };
  /** Current locale */
  locale?: string;
  /** Additional CSS classes */
  className?: string;
  /** Carousel container classes */
  carouselClassName?: string;
  /** Text container classes */
  textClassName?: string;
  /** Auto-rotate interval in ms */
  autoRotateInterval?: number;
  /** Max number of projects to show */
  maxProjects?: number;
  /** On project click handler */
  onProjectClick?: (project: PropertyDevelopmentProject) => void;
}

/**
 * Core ProjectHeroCarousel Component
 */
export function ProjectHeroCarousel({
  projects,
  title,
  subtitle,
  badgeText,
  stats,
  ctaText,
  ctaLink,
  messages,
  locale = 'en',
  className,
  carouselClassName,
  textClassName,
  autoRotateInterval = 5000,
  maxProjects = 5,
  onProjectClick,
}: ProjectHeroCarouselProps) {
  const prefersReducedMotion = useReducedMotion();
  
  // Limit projects
  const displayProjects = projects.slice(0, maxProjects);
  
  // Carousel state
  const [activeIndex, setActiveIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [direction, setDirection] = useState<'next' | 'prev'>('next');

  // Auto-rotate carousel
  useEffect(() => {
    if (displayProjects.length <= 1 || isHovered || prefersReducedMotion) return;

    const interval = setInterval(() => {
      setDirection('next');
      setActiveIndex((prev) => (prev + 1) % displayProjects.length);
    }, autoRotateInterval);

    return () => clearInterval(interval);
  }, [displayProjects.length, isHovered, autoRotateInterval, prefersReducedMotion]);

  // Handle navigation
  const goToNext = () => {
    setDirection('next');
    setActiveIndex((prev) => (prev + 1) % displayProjects.length);
  };

  const goToPrev = () => {
    setDirection('prev');
    setActiveIndex((prev) => (prev - 1 + displayProjects.length) % displayProjects.length);
  };

  const goToIndex = (index: number) => {
    setDirection(index > activeIndex ? 'next' : 'prev');
    setActiveIndex(index);
  };

  // Get text from messages with fallbacks - safely handle messages object
  const safeMessages = messages && typeof messages === 'object' ? messages : {};
  const heroTitle = title || (typeof safeMessages.title === 'string' ? safeMessages.title : 'Featured Projects');
  const heroSubtitle = subtitle || (typeof safeMessages.subtitle === 'string' ? safeMessages.subtitle : 'Discover our exceptional property developments');
  const heroBadge = badgeText || (typeof safeMessages.badge === 'string' ? safeMessages.badge : 'Now Showing');
  const viewProjectText = (typeof safeMessages.viewProject === 'string' ? safeMessages.viewProject : 'View Project');
  const startingFromText = (typeof safeMessages.startingFrom === 'string' ? safeMessages.startingFrom : 'Starting from');

  // Animation variants
  const slideVariants = {
    enter: (direction: 'next' | 'prev') => ({
      x: direction === 'next' ? 100 : -100,
      opacity: 0,
      scale: 0.8,
      rotateY: direction === 'next' ? 25 : -25,
    }),
    center: {
      x: 0,
      opacity: 1,
      scale: 1,
      rotateY: 0,
      zIndex: 10,
    },
    exit: (direction: 'next' | 'prev') => ({
      x: direction === 'next' ? -100 : 100,
      opacity: 0,
      scale: 0.8,
      rotateY: direction === 'next' ? -25 : 25,
    }),
  };

  if (displayProjects.length === 0) {
    return null;
  }

  const activeProject = displayProjects[activeIndex];
  
  if (!activeProject) {
    return null;
  }

  return (
    <section
      className={cn(
        'relative overflow-hidden py-16 lg:py-24',
        className
      )}
      aria-label="Featured projects hero"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-16">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-16 items-center">
          {/* LEFT: Text Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
            className={cn('space-y-6 lg:space-y-8', textClassName)}
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <span className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-blue-600/10 to-purple-600/10 px-4 py-2 text-sm font-semibold backdrop-blur-sm">
                <svg
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                  />
                </svg>
                {heroBadge}
              </span>
            </motion.div>

            {/* Title */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-4xl font-bold leading-tight tracking-tight sm:text-5xl lg:text-6xl xl:text-7xl"
            >
              {heroTitle}
            </motion.h1>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-lg leading-relaxed sm:text-xl lg:text-2xl opacity-80"
            >
              {heroSubtitle}
            </motion.p>

            {/* Stats (Optional) */}
            {stats && stats.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
                className="grid grid-cols-2 gap-6 lg:grid-cols-3"
              >
                {stats.map((stat, index) => (
                  <div key={index} className="space-y-1">
                    <div className="text-3xl font-bold lg:text-4xl">{stat.value}</div>
                    <div className="text-sm font-medium opacity-70">{stat.label}</div>
                  </div>
                ))}
              </motion.div>
            )}

            {/* CTA Button (Optional) */}
            {ctaText && ctaLink && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
              >
                <Link
                  href={ctaLink}
                  className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 px-8 py-4 text-base font-semibold text-white shadow-lg transition-all hover:scale-105 hover:shadow-xl"
                >
                  {ctaText}
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 8l4 4m0 0l-4 4m4-4H3"
                    />
                  </svg>
                </Link>
              </motion.div>
            )}

            {/* Navigation Dots - Visible on Mobile */}
            <div className="flex items-center gap-2 lg:hidden">
              {displayProjects.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToIndex(index)}
                  className={cn(
                    'h-2 rounded-full transition-all duration-300',
                    index === activeIndex ? 'w-8 bg-current' : 'w-2 opacity-40 hover:opacity-70'
                  )}
                  aria-label={`Go to project ${index + 1}`}
                />
              ))}
            </div>
          </motion.div>

          {/* RIGHT: Apple TV-Style Carousel */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
            className={cn('relative', carouselClassName)}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            {/* Carousel Container */}
            <div className="relative aspect-[3/4] w-full max-w-md mx-auto lg:max-w-none perspective-1000">
              {/* Background Cards (Stacked Effect) */}
              <div className="absolute inset-0 flex items-center justify-center">
                {displayProjects.slice(0, 3).map((project, index) => {
                  const isActive = index === 0;
                  const offset = (index + 1) * 8;
                  const scale = 1 - (index + 1) * 0.05;
                  const opacity = 1 - (index + 1) * 0.3;

                  return (
                    <div
                      key={project.id}
                      className="absolute inset-0 rounded-3xl shadow-2xl transition-all duration-500"
                      style={{
                        transform: `translateY(${offset}px) scale(${scale})`,
                        opacity: isActive ? 0 : opacity,
                        zIndex: 10 - index,
                      }}
                    >
                      <div className="relative h-full w-full overflow-hidden rounded-3xl bg-gradient-to-br from-gray-100 to-gray-200">
                        {project.heroImageUrl && (
                          <Image
                            src={project.heroImageUrl}
                            alt=""
                            fill
                            className="object-cover"
                            sizes="(max-width: 768px) 100vw, 50vw"
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Active Card (Animated) */}
              <div className="relative z-20 h-full">
                <AnimatePresence initial={false} custom={direction} mode="wait">
                  <motion.div
                    key={activeProject.id}
                    custom={direction}
                    variants={slideVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{
                      x: { type: 'spring', stiffness: 300, damping: 30 },
                      opacity: { duration: 0.3 },
                      scale: { duration: 0.4 },
                      rotateY: { duration: 0.4 },
                    }}
                    className="relative h-full w-full"
                  >
                    {/* Card */}
                    <Link
                      href={`/${locale}/projects/${activeProject.slug || activeProject.id}`}
                      className="group relative block h-full w-full overflow-hidden rounded-[2rem] bg-white shadow-[0_8px_30px_rgb(0,0,0,0.12)] transition-all duration-500 hover:shadow-[0_20px_60px_rgb(0,0,0,0.18)] hover:-translate-y-1"
                      onClick={() => onProjectClick?.(activeProject)}
                    >
                      {/* Image */}
                      <div className="relative h-2/3 w-full overflow-hidden">
                        {activeProject.heroImageUrl ? (
                          <Image
                            src={activeProject.heroImageUrl}
                            alt={activeProject.name}
                            fill
                            className="object-cover transition-all duration-700 group-hover:scale-[1.08]"
                            sizes="(max-width: 768px) 100vw, 50vw"
                            priority
                          />
                        ) : (
                          <div className="h-full w-full bg-gradient-to-br from-gray-100 to-gray-200" />
                        )}

                        {/* Subtle Gradient Overlay - Apple Style */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-60 group-hover:opacity-70 transition-opacity duration-500" />

                        {/* Status Badge - Refined */}
                        <motion.div 
                          className="absolute right-5 top-5"
                          whileHover={{ scale: 1.05 }}
                          transition={{ type: 'spring', stiffness: 400, damping: 17 }}
                        >
                          {activeProject.status === 'SOLD_OUT' || activeProject.availableUnits === 0 ? (
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/95 backdrop-blur-xl px-3.5 py-1.5 text-xs font-semibold text-gray-900 shadow-lg ring-1 ring-black/5">
                              <span className="h-1.5 w-1.5 rounded-full bg-red-500"></span>
                              {(messages?.soldOut as string) || 'Sold Out'}
                            </span>
                          ) : activeProject.status === 'UPCOMING' ? (
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/95 backdrop-blur-xl px-3.5 py-1.5 text-xs font-semibold text-gray-900 shadow-lg ring-1 ring-black/5">
                              <span className="h-1.5 w-1.5 rounded-full bg-blue-500"></span>
                              {(messages?.comingSoon as string) || 'Coming Soon'}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/95 backdrop-blur-xl px-3.5 py-1.5 text-xs font-semibold text-gray-900 shadow-lg ring-1 ring-black/5">
                              <span className="h-1.5 w-1.5 rounded-full bg-green-500"></span>
                              {activeProject.availableUnits} {(messages?.availableUnits as string) || 'Available'}
                            </span>
                          )}
                        </motion.div>
                      </div>

                      {/* Content - Modern Typography */}
                      <div className="relative h-1/3 p-7 flex flex-col justify-between bg-white">
                        <div className="space-y-3">
                          {/* Project Name - Large, Bold, Sans-Serif */}
                          <motion.h3 
                            className="text-2xl lg:text-3xl font-bold leading-tight text-gray-900 line-clamp-2 tracking-tight"
                            style={{ fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.1 }}
                          >
                            {activeProject.name}
                          </motion.h3>

                          {/* Price - Clean & Modern */}
                          {activeProject.displayPriceText && (
                            <motion.div 
                              className="flex items-baseline gap-2"
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              transition={{ duration: 0.5, delay: 0.2 }}
                            >
                              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {startingFromText}
                              </span>
                              <span className="text-xl lg:text-2xl font-bold text-gray-900" style={{ fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
                                {activeProject.displayPriceText}
                              </span>
                            </motion.div>
                          )}
                        </div>

                        {/* View Project Link - Micro-interaction */}
                        <motion.div 
                          className="flex items-center gap-2 text-sm font-semibold text-gray-900 pt-2"
                          style={{ fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}
                          whileHover={{ x: 4 }}
                          transition={{ type: 'spring', stiffness: 400, damping: 17 }}
                        >
                          <span className="relative">
                            {viewProjectText}
                            <motion.span 
                              className="absolute bottom-0 left-0 h-px bg-gray-900"
                              initial={{ width: 0 }}
                              whileHover={{ width: '100%' }}
                              transition={{ duration: 0.3 }}
                            />
                          </span>
                          <motion.svg 
                            className="h-4 w-4" 
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                            animate={{ x: [0, 4, 0] }}
                            transition={{ 
                              duration: 1.5, 
                              repeat: Infinity, 
                              repeatType: 'loop',
                              ease: 'easeInOut'
                            }}
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                          </motion.svg>
                        </motion.div>
                      </div>
                    </Link>
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Navigation Arrows */}
              {displayProjects.length > 1 && (
                <>
                  <button
                    onClick={goToPrev}
                    className="absolute -left-4 top-1/2 z-30 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 shadow-lg backdrop-blur-sm transition-all hover:bg-white hover:scale-110 lg:flex"
                    aria-label="Previous project"
                  >
                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button
                    onClick={goToNext}
                    className="absolute -right-4 top-1/2 z-30 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 shadow-lg backdrop-blur-sm transition-all hover:bg-white hover:scale-110 lg:flex"
                    aria-label="Next project"
                  >
                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </>
              )}

              {/* Navigation Dots - Desktop */}
              <div className="absolute -bottom-12 left-1/2 hidden -translate-x-1/2 items-center gap-2 lg:flex">
                {displayProjects.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToIndex(index)}
                    className={cn(
                      'h-2 rounded-full transition-all duration-300',
                      index === activeIndex ? 'w-8 bg-current' : 'w-2 opacity-40 hover:opacity-70'
                    )}
                    aria-label={`Go to project ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

